/* eslint-disable react/jsx-props-no-spreading, jsdoc/require-returns-check, jsdoc/check-param-names -- forwardRef destructured props */
import { forwardRef } from 'react';

// Utils
import cn from '../../../utils/cn';

// Styles
import style from './Checkbox.module.css';

/**
 * @typedef {'primary'|'neutral'|'black'|'neutral-to-primary'|'secondary'|'identitary'} CheckboxTheme
 */

/**
 * @typedef {'small'|'large'} CheckboxSize
 */

const Checkbox = forwardRef(
/**
 * @param {object} props - Component props.
 * @param {string} props.id - Required unique identifier for the checkbox
 * @param {boolean} props.checked - Whether checkbox is checked
 * @param {boolean} [props.indeterminate=false] - Partial selection UI (trait horizontal ; ignorer si checked)
 * @param {string} [props.className] - Additional CSS classes
 * @param {string | React.ReactNode} [props.label] - Text label for the checkbox
 * @param {CheckboxSize} [props.size = 'large'] - Checkbox size
 * @param {CheckboxTheme} [props.theme = 'primary'] - Checkbox theme
 * @param {boolean} [props.disabled = false] - Whether checkbox is disabled
 * @param {boolean} [props.isReadOnly = false] - Locked; value remains in form
 * @param {boolean} [props.multiline = false] - Allow label to wrap and grow row height (e.g. composite labels)
 * @param {React.ChangeEventHandler<HTMLInputElement>} [props.onChange] - Callback when checkbox is changed
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element
 * @returns {JSX.Element} Checkbox component
 */
  ({
    checked = false,
    indeterminate = false,
    className,
    label,
    size = 'large',
    theme = 'primary',
    disabled = false,
    isReadOnly = false,
    multiline = false,
    onChange = () => {},
    ...props
  }, ref) => (
    <label
      title={typeof label === 'string' ? label : undefined}
      htmlFor={props.id}
      className={
        cn([
          style['checkbox-container'],
          ...(label ? [style['has-label']] : []),
          style[size],
          style[theme],
          ...(indeterminate && !checked ? [style['checkbox-indeterminate']] : []),
          ...(multiline ? [style.multiline] : []),
          ...(isReadOnly ? [style['is-read-only']] : []),
          ...(className ? [className] : []),
        ])
      }
    >
      <input
        ref={ref}
        type="checkbox"
        className={style['checkbox-input']}
        disabled={disabled}
        checked={checked}
        onChange={isReadOnly ? () => {} : onChange}
        aria-checked={indeterminate ? 'mixed' : undefined}
        {...props}
      />
      <span className={cn([style['checkbox-checkmark']])} />
      {label && <span className={style['checkbox-label']}>{label}</span>}
    </label>
  ),
);

export default Checkbox;
